import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import cn from 'classnames';
import { FilterBar } from '@Components/FilterBar';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState/TableLoading';
import { useSetError } from '@src/redux/modals';
import { PacingBar, PacingBarMarker } from '@Pages/Pacing/components/PacingBar';
import { PacingPane } from '@Pages/Pacing/components/PacingPane';
import { PacingBarColors } from '@src/utils/colors';
import {
  deliveryLayers,
  pacingKpis,
  pacingLabel,
  reprocessingLayers,
  NO_COMMISSION_OR_PRELOGS_WARNING,
  NO_PRELOGS_WARNING
} from './components/bar-layers';
import { CsvDownloadButton } from './components/CsvDownloadButton';
import { CompanyLabel, NetworkLabel } from './components/PacingLabels';
import { PacingOverlay } from './components/PacingOverlay';
import { LoadFailedState, RowsSkeleton } from './components/PacingSkeletons';
import { totalsRenderer } from './components/totals-renderer';
import {
  ACCEPTED_LABEL,
  AcceptedDimension,
  COMPANY_DIMENSION,
  COMPANY_LABEL,
  MediaTypeDimension,
  NETWORK_DIMENSION,
  NETWORK_LABEL,
  PrimaryDimension,
  SpendDimension
} from './linear-pacing-constants';
import { calculateAdjustedSpend, paneTitle, uniqueLines } from './linear-pacing-utils';
import {
  LinearPacingFlight,
  LinearPacingLogRow,
  LinearPacingRow,
  LinearPacingTotals,
  useLinearPacingDetail,
  useLinearPacingLogs
} from './use-linear-pacing';

const CSV_HEADER = [
  'Date',
  COMPANY_LABEL,
  NETWORK_LABEL,
  'Avail',
  'Booked',
  'Logs',
  'Estimated',
  'Has BVS',
  'Has Prelogs',
  'Last Prelog Day'
];

const BOTTOM_SUPER_HEADERS = [
  { span: 6, data: 'Summary' },
  { span: 3, data: 'Ordered' },
  { span: 9, data: 'Logs' }
];

type RowFilter = (line: object) => boolean;
type LeftItem = LinearPacingRow & { key: string };
type RightItem = LinearPacingFlight & { key: string };

const DAY_TOTAL_KEYS = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'] as const;

// The totals row holds type-prefixed strings ("$123", "#4", "%0.9") that totalsRenderer parses
// back into formatted numbers.
type LogTotalsRow = Partial<
  Record<keyof LinearPacingLogRow | (typeof DAY_TOTAL_KEYS)[number] | 'logSpend' | 'pct', string>
>;

const sum = (values: number[]): number => values.reduce((total, value) => total + value, 0);
const logSpendOf = (row: LinearPacingLogRow): number => sum(row.spots.map(spot => spot.cost));

const estimatedOf = (flight: LinearPacingTotals, acceptedDimension: AcceptedDimension): number =>
  acceptedDimension === ACCEPTED_LABEL ? flight.estimated.spend : flight.estimatedAll.spend;

const spendsOf = (
  flight: LinearPacingTotals,
  acceptedDimension: AcceptedDimension,
  spendDimension: SpendDimension
) => {
  const accepted = acceptedDimension === ACCEPTED_LABEL;
  // No commission fallback here (unlike By Campaign): a null commission nets at 0.85.
  const calc = (spend: number) =>
    calculateAdjustedSpend(spend, flight.commission, spendDimension, 0);
  return {
    estimatedSpend: calc(estimatedOf(flight, acceptedDimension)),
    logsSpend: calc(accepted ? flight.logs.spend : flight.logsAll.spend),
    unsecuredLogsSpend: calc(accepted ? flight.unsecuredLogs.spend : flight.unsecuredLogsAll.spend),
    securedLogsSpend: calc(accepted ? flight.securedLogs.spend : flight.securedLogsAll.spend),
    bookedSpend: calc(flight.booked.spend),
    budgetSpend: flight.tvBudget ? calc(flight.tvBudget) : undefined
  };
};

const csvKeys = (acceptedDimension: AcceptedDimension) =>
  acceptedDimension === ACCEPTED_LABEL
    ? ({ logsKey: 'logs', estimatedKey: 'estimated' } as const)
    : ({ logsKey: 'logsAll', estimatedKey: 'estimatedAll' } as const);

// Booked, logs and estimated days, in that first-seen order.
const flightDays = (flight: LinearPacingTotals, acceptedDimension: AcceptedDimension) => {
  const { logsKey, estimatedKey } = csvKeys(acceptedDimension);
  return [
    ...new Set([
      ...Object.keys(flight.booked?.days ?? {}),
      ...Object.keys(flight[logsKey]?.days ?? {}),
      ...Object.keys(flight[estimatedKey]?.days ?? {})
    ])
  ];
};

// One CSV row per flight-day, in CSV_HEADER's columns. Both panes' downloads use it.
const flightDayCsvRow = (
  flight: LinearPacingTotals,
  date: string,
  company: unknown,
  network: unknown,
  acceptedDimension: AcceptedDimension
): unknown[] => {
  const { logsKey, estimatedKey } = csvKeys(acceptedDimension);
  return [
    date,
    company,
    network,
    '', // Avail (not in data)
    flight.booked?.days?.[date]?.spend || 0,
    flight[logsKey]?.days?.[date]?.spend || 0,
    flight[estimatedKey]?.days?.[date]?.spend || 0,
    flight.hasBvs ? 'Yes' : 'No',
    flight.hasPrelogs ? 'Yes' : 'No',
    flight.lastPrelogDay || ''
  ];
};

// Largest |booked - estimated| first.
const byPacingGap =
  (acceptedDimension: AcceptedDimension) => (a: LinearPacingTotals, b: LinearPacingTotals) =>
    Math.abs(b.booked.spend - estimatedOf(b, acceptedDimension)) -
    Math.abs(a.booked.spend - estimatedOf(a, acceptedDimension));

interface SpendProps {
  flight: LinearPacingTotals;
  acceptedDimension: AcceptedDimension;
  spendDimension: SpendDimension;
}

const renderOverlay = (
  title: string,
  { flight, acceptedDimension, spendDimension }: SpendProps
) => {
  const {
    estimatedSpend,
    logsSpend,
    unsecuredLogsSpend,
    securedLogsSpend,
    bookedSpend,
    budgetSpend
  } = spendsOf(flight, acceptedDimension, spendDimension);
  return (
    <PacingOverlay
      title={title}
      reprocessing={flight.logs.spend === 0 && bookedSpend > 1000}
      logs={logsSpend}
      estimated={estimatedSpend}
      booked={bookedSpend}
      secured={securedLogsSpend || undefined}
      unsecured={securedLogsSpend ? unsecuredLogsSpend : undefined}
      targets={
        flight.tvBudget
          ? {
              budget: Number(budgetSpend),
              assumedClearanceRate: Number(flight.assumedClearanceRate)
            }
          : undefined
      }
    />
  );
};

type Spends = ReturnType<typeof spendsOf>;

// Client Detail's left bars: the clearance-adjusted booked target, plus the client budget.
const targetMarkers =
  (flight: LinearPacingTotals) =>
  ({ bookedSpend, budgetSpend }: Spends): PacingBarMarker[] => [
    {
      key: 'booked-target',
      value: bookedSpend * Number(flight.assumedClearanceRate),
      color: PacingBarColors.bookedTarget
    },
    ...(budgetSpend
      ? [{ key: 'client-target', value: budgetSpend, color: PacingBarColors.clientTarget }]
      : [])
  ];

const bookedMarkers = ({ bookedSpend }: Spends): PacingBarMarker[] => [
  { key: 'booked', value: bookedSpend, color: PacingBarColors.booked }
];

interface FlightPacingBarProps extends SpendProps {
  title: string;
  max: number;
  // Markers drawn when the flight has spend (none while reprocessing).
  markers: (spends: Spends) => PacingBarMarker[];
  alert: ReactNode;
}

const FlightPacingBar = ({
  title,
  flight,
  acceptedDimension,
  spendDimension,
  max,
  markers,
  alert
}: FlightPacingBarProps) => {
  const spends = spendsOf(flight, acceptedDimension, spendDimension);
  const { estimatedSpend, logsSpend, unsecuredLogsSpend, securedLogsSpend, bookedSpend } = spends;
  const hasSpend = logsSpend > 0 || estimatedSpend > 0;

  return (
    <PacingBar
      max={max}
      layers={
        hasSpend
          ? deliveryLayers({
              secured: securedLogsSpend,
              unsecured: unsecuredLogsSpend,
              estimated: estimatedSpend
            })
          : reprocessingLayers(max)
      }
      markers={hasSpend ? markers(spends) : []}
      endLabel={hasSpend && pacingLabel(estimatedSpend, bookedSpend)}
      alert={alert}
      overlay={renderOverlay(title, { flight, acceptedDimension, spendDimension })}
      ariaLabel={hasSpend ? `${title} pacing: ${pacingLabel(estimatedSpend, bookedSpend)}` : title}
    />
  );
};

// "17:30-23:59" → "5:30p-11:59p"
const formatDaypart = (daypart: string): string =>
  daypart
    .split('-')
    .map(time =>
      Dfns.format('h:mma', Dfns.parseISO(`2015-01-01T${time}`))
        .replace('M', '')
        .toLowerCase()
    )
    .join('-');

const logsDayHeader = (dow: string, index: number): TypedHeader<LinearPacingLogRow> => ({
  label: dow,
  name: dow,
  contentGetter: row => row.spots[index].cost,
  renderer: row => <NumberFormatter value={row.spots[index].cost} type='$' decimals={0} />,
  width: 70
});

const BOTTOM_HEADERS: TypedHeader<LinearPacingLogRow>[] = [
  { label: 'Avail', name: 'avail', width: 70 },
  { label: 'Len', name: 'length', width: 70 },
  { label: 'Days', name: 'dow', flex: 1 },
  {
    label: 'Daypart',
    name: 'daypart',
    flex: 1,
    renderer: row => row.daypart && formatDaypart(row.daypart)
  },
  {
    label: 'Rotation',
    name: 'rotation',
    flex: 1,
    renderer: row => row.rotation && (row.rotation.match(/(.*) \(.*/)?.[1] ?? null)
  },
  { label: 'Type', name: 'type' },
  {
    label: '#',
    name: 'count',
    renderer: row => <NumberFormatter value={row.count} type='#' decimals={0} />,
    width: 50
  },
  {
    label: 'Cost',
    name: 'cost',
    renderer: row => <NumberFormatter value={row.cost} type='$' decimals={0} />
  },
  {
    label: 'Spend',
    name: 'spend',
    renderer: row => <NumberFormatter value={row.spend} type='$' decimals={0} />
  },
  ...DAY_TOTAL_KEYS.map(logsDayHeader),
  {
    label: 'Total',
    name: 'logSpend',
    renderer: row => <NumberFormatter value={logSpendOf(row)} type='$' decimals={0} />
  },
  {
    label: '%',
    name: 'pct',
    renderer: row =>
      row.spend ? (
        <NumberFormatter value={logSpendOf(row) / row.spend} type='%' decimals={0} />
      ) : (
        '—'
      ),
    width: 70
  }
];

const makeLogTotals = (rows: LinearPacingLogRow[]): LogTotalsRow => {
  const logSpend = sum(rows.map(logSpendOf));
  const bookedSpend = sum(rows.map(row => row.spend));
  return {
    count: `#${sum(rows.map(row => row.count))}`,
    spend: `$${bookedSpend}`,
    ...Object.fromEntries(
      DAY_TOTAL_KEYS.map((key, index) => [key, `$${sum(rows.map(row => row.spots[index].cost))}`])
    ),
    logSpend: `$${logSpend}`,
    pct: `%${logSpend / bookedSpend}`
  };
};

const renderCompany = ({ key }: { key: string }) => <CompanyLabel company={key} />;
const renderNetwork = ({ key }: { key: string }) => <NetworkLabel network={key} />;

interface LinearPacingDetailProps {
  spendDimension: SpendDimension;
  acceptedDimension: AcceptedDimension;
  mediaTypeDimension: MediaTypeDimension;
  leftKey: PrimaryDimension;
  start: string;
  // Owned by the page, which shows them as breadcrumbs.
  selectedLeft?: string;
  setSelectedLeft: (key?: string) => void;
  selectedRight?: string;
  setSelectedRight: (key?: string) => void;
}

export const LinearPacingDetail = ({
  spendDimension,
  acceptedDimension,
  mediaTypeDimension,
  leftKey,
  start,
  selectedLeft,
  setSelectedLeft,
  selectedRight,
  setSelectedRight
}: LinearPacingDetailProps) => {
  const rightKey = leftKey === COMPANY_DIMENSION ? NETWORK_DIMENSION : COMPANY_DIMENSION;
  const [filters, setFilters] = useState<{ left: RowFilter; right: RowFilter }>({
    left: () => true,
    right: () => true
  });
  const [filteredBottomData, setFilteredBottomData] = useState<LinearPacingLogRow[]>();

  const setError = useSetError();
  const { data, error } = useLinearPacingDetail({
    start,
    grouping: leftKey,
    mediaType: mediaTypeDimension
  });
  useEffect(() => {
    if (error) {
      setError({ message: 'Could not fetch data for pacing detail page', reportError: error });
    }
  }, [error, setError]);

  const { data: bottomData, error: bottomError } = useLinearPacingLogs({
    week: start,
    company: leftKey === COMPANY_DIMENSION ? selectedLeft : selectedRight,
    network: leftKey === COMPANY_DIMENSION ? selectedRight : selectedLeft,
    mediaType: mediaTypeDimension
  });
  useEffect(() => {
    if (bottomError) {
      setError({
        message: 'Could not fetch detailed data for pacing detail page',
        reportError: bottomError
      });
    }
  }, [bottomError, setError]);

  useEffect(() => {
    setFilteredBottomData(bottomData);
  }, [bottomData]);

  const renderLogsLoading = useCallback(
    () => (
      <TableLoading
        isLoading={!bottomData && !bottomError}
        error={bottomError}
        emptyMessage='No logs for this selection'
      />
    ),
    [bottomData, bottomError]
  );

  const {
    leftMax = 0,
    leftData,
    leftCsv = []
  } = useMemo(() => {
    // Don't render a response whose shape doesn't match the current grouping.
    if (
      !data ||
      (leftKey === COMPANY_DIMENSION && typeof data[0]?.network === 'string') ||
      (leftKey === NETWORK_DIMENSION && typeof data[0]?.company === 'string')
    ) {
      return {};
    }

    let max = 0;
    const visible = selectedLeft
      ? data.filter(item => item[leftKey] === selectedLeft)
      : data.filter(filters.left);
    const items = visible.flatMap((item): LeftItem[] => {
      max = Math.max(max, item.booked.spend, estimatedOf(item, acceptedDimension));
      const key = item[leftKey];
      return Array.isArray(key) ? [] : [{ ...item, key }];
    });
    items.sort(byPacingGap(acceptedDimension));

    // Every flight in the week, ignoring the left filter.
    const csvRows: unknown[][] = [CSV_HEADER];
    data.forEach(row => {
      const flights = row[rightKey];
      if (!Array.isArray(flights)) {
        return;
      }
      flights.forEach(flight => {
        const company = leftKey === COMPANY_DIMENSION ? row.company : flight.company;
        const network = leftKey === COMPANY_DIMENSION ? flight.network : row.network;
        flightDays(flight, acceptedDimension).forEach(date => {
          csvRows.push(flightDayCsvRow(flight, date, company, network, acceptedDimension));
        });
      });
    });

    return { leftMax: max, leftData: items, leftCsv: csvRows };
  }, [data, selectedLeft, filters.left, leftKey, rightKey, acceptedDimension]);

  const {
    rightMax = 0,
    rightData,
    rightDataFiltered,
    rightCsv = []
  } = useMemo(() => {
    const selectedRow = data?.find(row => row[leftKey] === selectedLeft);
    if (!selectedLeft || !selectedRow) {
      return {};
    }
    const flights = selectedRow[rightKey];
    const allFlights = Array.isArray(flights) ? flights : [];
    const filteredFlights = selectedRight
      ? allFlights.filter(flight => flight[rightKey] === selectedRight)
      : allFlights.filter(filters.right);

    const csvRows: unknown[][] = [
      CSV_HEADER,
      ...filteredFlights.flatMap(flight =>
        flightDays(flight, acceptedDimension)
          .sort()
          .map(day =>
            flightDayCsvRow(
              flight,
              day,
              leftKey === COMPANY_DIMENSION ? selectedLeft : flight.company,
              leftKey === COMPANY_DIMENSION ? flight.network : selectedLeft,
              acceptedDimension
            )
          )
      )
    ];

    let max = 0;
    const items = filteredFlights.map((flight): RightItem => {
      max = Math.max(max, flight.booked.spend, estimatedOf(flight, acceptedDimension));
      return { ...flight, key: flight[rightKey] ?? '' };
    });
    items.sort(byPacingGap(acceptedDimension));

    return {
      rightMax: max,
      rightData: allFlights,
      rightDataFiltered: items,
      rightCsv: csvRows
    };
  }, [data, selectedLeft, selectedRight, filters.right, leftKey, rightKey, acceptedDimension]);

  const bottomTotals = useMemo(
    () => makeLogTotals(filteredBottomData ?? bottomData ?? []),
    [filteredBottomData, bottomData]
  );

  if (!leftData) {
    return (
      <div className='pacing-dashboard'>
        <div className='pacing-panes'>
          <div className='pacing-left-pane'>{error ? <LoadFailedState /> : <RowsSkeleton />}</div>
        </div>
      </div>
    );
  }

  const selectedFlight = leftData.find(item => item.key === selectedLeft);
  const selectedSpends =
    selectedFlight && spendsOf(selectedFlight, acceptedDimension, spendDimension);

  const spendProps = { acceptedDimension, spendDimension };
  const renderLeftBar = (item: LeftItem) => (
    <FlightPacingBar
      title={item.key}
      flight={item}
      max={leftMax}
      markers={targetMarkers(item)}
      alert={item.commission == null && !item.hasPrelogs && NO_COMMISSION_OR_PRELOGS_WARNING}
      {...spendProps}
    />
  );
  const renderRightBar = (item: RightItem) => (
    <FlightPacingBar
      title={item.key}
      flight={item}
      max={rightMax}
      markers={bookedMarkers}
      alert={!item.hasPrelogs && NO_PRELOGS_WARNING}
      {...spendProps}
    />
  );

  return (
    <div className='pacing-dashboard'>
      <div className='pacing-panes'>
        <div className={cn('pacing-left-pane', { condensed: selectedLeft })}>
          <PacingPane
            title={selectedLeft ? undefined : paneTitle(leftKey, leftData.length)}
            items={leftData}
            selectedKey={selectedLeft}
            onSelect={key => {
              setSelectedRight(undefined);
              setSelectedLeft(key);
              setFilteredBottomData(undefined);
            }}
            controls={
              selectedLeft ? undefined : (
                <>
                  <FilterBar
                    hasAdvanced
                    storageKeySuffix='linear-pacing-detail-left'
                    options={[leftKey]}
                    lines={uniqueLines(data ?? [], leftKey)}
                    onFilter={filter => setFilters(current => ({ ...current, left: filter }))}
                  />
                  {leftData.length > 0 && (
                    <CsvDownloadButton
                      filename={`Linear Pacing for week of ${start}`}
                      rows={() => leftCsv}
                    />
                  )}
                </>
              )
            }
            labelRenderer={leftKey === COMPANY_DIMENSION ? renderCompany : renderNetwork}
            lineItemRenderer={selectedLeft ? undefined : renderLeftBar}
          />
        </div>
        {selectedLeft && (
          <div className='pacing-right-pane'>
            {!rightData ? (
              <div className='pacing-top-pane'>
                <RowsSkeleton />
              </div>
            ) : (
              <>
                <div className={cn('pacing-top-pane', { condensed: selectedRight })}>
                  <PacingPane
                    title={
                      selectedRight ? undefined : paneTitle(rightKey, rightDataFiltered.length)
                    }
                    kpis={
                      selectedRight || !selectedSpends
                        ? undefined
                        : pacingKpis(
                            selectedSpends.bookedSpend,
                            selectedSpends.logsSpend,
                            selectedSpends.estimatedSpend
                          )
                    }
                    items={rightDataFiltered}
                    selectedKey={selectedRight}
                    onSelect={key => {
                      setSelectedRight(key);
                      setFilteredBottomData(undefined);
                    }}
                    controls={
                      selectedRight ? undefined : (
                        <>
                          <FilterBar
                            hasAdvanced
                            storageKeySuffix='linear-pacing-detail-right'
                            options={[rightKey]}
                            lines={uniqueLines(rightData, rightKey)}
                            onFilter={filter =>
                              setFilters(current => ({ ...current, right: filter }))
                            }
                          />
                          {rightDataFiltered.length > 0 && (
                            <CsvDownloadButton
                              filename={`Linear Pacing for ${selectedLeft} week of ${start}`}
                              rows={() => rightCsv}
                            />
                          )}
                        </>
                      )
                    }
                    labelRenderer={leftKey === COMPANY_DIMENSION ? renderNetwork : renderCompany}
                    lineItemRenderer={renderRightBar}
                  />
                </div>
                {selectedRight && (
                  <div className='pacing-bottom-pane' data-testid='linear-pacing-logs'>
                    {/* Union type: the totals row holds strings where data rows hold numbers. */}
                    <BPMTable<LinearPacingLogRow | LogTotalsRow, string>
                      alternateColors
                      storageKeySuffix='linear-pacing-detail-logs'
                      headers={BOTTOM_HEADERS}
                      superHeaders={BOTTOM_SUPER_HEADERS}
                      data={bottomData ?? []}
                      totals={bottomTotals}
                      totalsRenderer={totalsRenderer}
                      noRowsRenderer={renderLogsLoading}
                      onFilteredDataChange={(rows: LinearPacingLogRow[]) =>
                        setFilteredBottomData(current => (R.equals(current, rows) ? current : rows))
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
